<script lang="ts" context="module">
	type SideNavItems = SideNavMainItem | SideNavMainItem[] | undefined;
	type SideNavItemsComplete = SideNavItems | Promise<SideNavItems>;

	const componentProcessors: Record<GroceryLandingSection, {
		// sidenav: (section: ValueFromArray<StoreLanding["layout"]>) => SideNavMainItem | SideNavMainItem[] | undefined,
			sidenav: (...section: any[]) => SideNavItemsComplete,
	} | undefined> = {
		[GroceryLandingSection.Deals]: undefined,
		[GroceryLandingSection.BuyAgain]: { "sidenav": processorBuyAgain },
		[GroceryLandingSection.SavedItems]: { "sidenav": processorSavedItems }, //not in the designs :(
		[GroceryLandingSection.Aisles]: { "sidenav": processorAisles },
		[GroceryLandingSection.Banners]: undefined,
		[GroceryLandingSection.Occasions]: undefined,
	};
</script>
<script lang="ts">
	import type { GrocLandingItemsParams, LandingItemContext } from '$lib/def/landing';
	import type { Cache } from '$lib/serv/core/cache';
	import type { Page } from '@sveltejs/kit';

	import { goto } from '$app/navigation';
	import { GroceryLandingSection } from '$lib/def/landing';
	import { profile$ } from '$lib/serv/core/auth';
	import { liCache } from '$lib/serv/core/conf';
	import { setItem } from '$lib/serv/core/local-store';
	import { getLSCart, loadCart, setCartSeed, updateUser } from '$lib/serv/groc-cart';
	import { CacheAgeLong, CacheAgeMedium, CacheAgeShort, loadBase, obsGeoUpdates, stores$, streamSelectedStore } from "$lib/serv/groc-landing";
	import { createCartAndStoreFollowup, createCartAndStoreOutcome } from '$lib/serv/groc-store';
	import { LOCATION_PROPS, landingItemsParams$ } from '$lib/serv/locations';
	import { getSideNavContext, type SideNavItem, type SideNavMainItem } from '$lib/serv/nav';
	import { showDetail, showPDP$ } from '$lib/ui/home/groc/pdp/Main.svelte';
	import { page$ } from '$lib/util/obs';
	import { type Query, query } from '$lib/util/url';
	import { combineLatest, delay, distinctUntilChanged, filter, map, of, shareReplay, switchMap, tap, withLatestFrom } from "rxjs";
	import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
	import { type Component, onDestroy, setContext } from "svelte";
	import { KEY_AISLE, KEY_SUB_AISLE } from './groc/Aisle.svelte';
	import { KEY_VERTICAL, Vertical } from '$lib/def/verticals';
	import { KEY_SEARCH } from '$lib/serv/food-landing';
	
	import GrocSearch from "$lib/ui/home/groc/Search.svelte";
	import GrocPDP from '$lib/ui/modals/GrocPdpModal.svelte';
	import Carousel from '../layout/Carousel.svelte';
	import Sidenav from '../layout/Sidenav.svelte';
	import SidenavHeader from '../layout/SidenavHeader.svelte';
	import LoadingIcons from '../shared/LoadingIcons.svelte';
	import Modal from '../shared/Modal.svelte';
	import Aisles, { fetchAisleData, processDataForSideNavUpdate as processorAisles } from "./groc/Aisles.svelte";
	import Banners, { KEY_BANNER } from './groc/Banners.svelte';
	import Notification from './groc/Notification.svelte';
	import Occasions, { KEY_OCCASION } from './groc/Occasions.svelte';
	import OutOfRange from './groc/OutOfRange.svelte';
	import UserSpecificAisle, { BUY_AGAIN } from './groc/UserSpecificAisle.svelte';
	import BuyAgain, { processDataForSideNavUpdate as processorBuyAgain } from './groc/swimlanes/BuyAgain.svelte';
	import Deals from './groc/swimlanes/Deals.svelte';
	import SavedItems, { processDataForSideNavUpdate as processorSavedItems } from './groc/swimlanes/SavedItems.svelte';
	import SearchInput from '$lib/ui/home/SearchInput.svelte';
  import StoreSelect from './groc/StoreSelect.svelte';
	import CarouselV2 from '../layout/CarouselV2.svelte';

	export let page: Page;

	const context = {
		landingItems: `ca://grocery/landing-items/stores`,
		products: `ca://grocery/products/fill_layout`,
		cacheAge: liCache.value() ? {
			short: CacheAgeShort, medium: CacheAgeMedium, long: CacheAgeLong
		} : {
			short: -1, medium: -1, long: -1
		}
	};
	setContext<LandingItemContext>('endpoints', context);

	const sideNavManager = getSideNavContext();
	const navState$ = sideNavManager.createSideNavItemsStream(page$)

	const components: { type: GroceryLandingSection, component: Component<any> }[] = [
		{ type: GroceryLandingSection.Banners, component: Banners },
		{ type: GroceryLandingSection.Aisles, component: Aisles },
		{ type: GroceryLandingSection.Occasions, component: Occasions },
	];

	const componentFromType = (type: GroceryLandingSection): Component<any> | undefined => components.find(c => c.type === type)?.component;

	$: selectedStore$ = streamSelectedStore(of(page.url));

	$: params = page.url.searchParams;
  $: searchParams = query(page.url).toObject();
  $: search = page.url.searchParams.get('search') ?? undefined;
  $: showSearch = search !== undefined;
	$: selectedLabel = page.url.searchParams.get('label') ?? '';

	const browsingStore$ = createCartAndStoreFollowup(
		createCartAndStoreOutcome(
			stores$.pipe(
				filter(cache => !cache.loading),
				map(({ payload, location }) => ({ stores: payload, location })),
			),
			getLSCart(),
			profile$.pipe(/*tap(u => console.log('user cart', u)),*/switchMap(u => u ? loadCart() : of(undefined))),
		),
		{ next: ({store, location}) => {
			if (!location?.latitude || !location?.longitude) {
				query(page.url).set('store', store).go();
			} else {
				const update = LOCATION_PROPS.map(prop => [prop, location[prop]] as [string, unknown]);
				const _q = query(page.url).setBulk(location);
				const q = update.reduce((acc, [k,v]) => acc.set(k, v?.toString()), query(page.url))
				q.set('store', store);
				goto('?' + q.toString(), { replaceState: true, noScroll: true, keepFocus: true }); // redirect to cart store
			}
		}},
	).pipe(
		tap(({cart}) => setCartSeed(cart)),
	);

	let landingItems$ = combineLatest([
		obsGeoUpdates(landingItemsParams$),
		browsingStore$,
		profile$.pipe(delay(0)), // delay to prevent race condition with logout clearing IDB
	]).pipe(
		// tap(([params, store, profile]) => updateCartCustomer(buildCartCustomer(params, profile))), // remove in favour of newer cart mechanism
		tap(([,store]) => updateUser(store.cart)),
		// tap(({store}) => selectedStore = store),
		// filter(({ store }) => typeof store !== 'undefined'), // from `browsingStore$` emits undefined if out of area; otherwise the local default store 
		switchMap(([loc, {store}]) => {
			if (!store?.id) return of({ loading: false, payload: null, error: null, store: null } as Cache<null> & { store: null });
			const params = { ...loc, store: store.id };
			return loadBase(params as GrocLandingItemsParams, true).pipe(map(l => ({ ...l, store })));
		}),
		// tap(({store}) =>  updateCartStore(DEFAULT_MERCHANT_KEY, store!)), // remove in favour of newer cart mechanism
		shareReplay(1),
	);

	const swimlaneNavItems:SideNavItem[] = [];
	const swimlaneNavItemUpdates = new BehaviorSubject<SideNavItem[]>(swimlaneNavItems);
	

	let aisleDataLoader$ = landingItems$.pipe(
		filter((loader) => !!loader.payload),
		map(({payload}) => payload?.layout.find(({type}) => type === GroceryLandingSection.Aisles)),
		filter(aisles => !!aisles),
		// tap(() => console.log('TAP')),
		switchMap(aisles => fetchAisleData(context.landingItems + aisles?.endpoint, context.cacheAge.medium)),
		filter(({payload}) => !!payload),
		withLatestFrom(swimlaneNavItemUpdates),
		tap(([{payload}, baseitems]) => {
			// const arNav = processAisleDataForSideNavUpdate(payload);
			// sideNavManager.setMainItems(arNav ? [...baseitems,...arNav] : baseitems);
		}),
		map(([rtn]) => rtn),
		shareReplay(1),
	);
  if (page.url.searchParams.has('catalogue_key')) {
    const key = page.url.searchParams.get('catalogue_key');
    showDetail(key);
  }

	const sidenavUrlParams$ = page$.pipe(
		map(page => query(page.url)),
		distinctUntilChanged((a, b) => [KEY_AISLE, KEY_OCCASION, KEY_BANNER, BUY_AGAIN].every(k => a.get(k) === b.get(k))),
	);
	const navMainItemsSub = landingItems$.pipe(
		withLatestFrom(sidenavUrlParams$),
	).subscribe({
		next: async ([{payload}, q]) => {
			if (!payload) return;
			const navItems: SideNavMainItem[] = [
				{
					id: 'home',
					title: 'Browse all',
					icon: 'icon-house-20',
					setHref: (page, item) => query(page.url).remove(KEY_AISLE, KEY_SUB_AISLE, KEY_OCCASION, KEY_BANNER, BUY_AGAIN).toUrl(),
					active: (page, item) => !query(page.url).hasAny(KEY_AISLE, KEY_OCCASION, KEY_BANNER, BUY_AGAIN),
				},
			];
			await Promise.all(
				payload.layout.map(async (section) => {				
					// Assume that each component has a `processDataForSideNavUpdate` function and invoke it to get the side nav items
					if (section.type in componentProcessors) {
						const item = await componentProcessors[section.type]?.sidenav(section);
						if (item) {
							if (Array.isArray(item)) {
								navItems.push(...item);
							} else {
								navItems.push(item);
							}
						}
					}
				})
			);

			sideNavManager.setMainItems(navItems);
		},
	});

	const insertSideNavUrl = (id:string, title:string, url:string, isActive: (targetUrl:URL|string) => boolean) => {
		swimlaneNavItems.push({
			id,
			title, // TODO: to be updates once the title arrives
			// href: url,
			setHref: (page, item) => url,
			active: (page, item) => isActive(page.url),
			// scroll: false,
		})
		// swimlaneNavItemUpdates.next(swimlaneNavItems)
	}

	function closeGrocSearch() {
    if ($showPDP$) showDetail(null);
    query(page.url).remove('search').go({ noScroll: true });
  }

	let openMenuModal = false;
	const closeContextMenu = () => {
		setItem('showContextMenu', false);
		openMenuModal = false;
	}
	let showStoreSelect = false;

	/**
	 * Removes address and vertical and returns true if any params remain
	*/ 
	function hasNoneAddressParams(query: Query) {
		return Object.keys(query.remove(...LOCATION_PROPS, KEY_VERTICAL).toObject()).length > 0;
	}

	function backButtonUrl(url: URL) {
		if (hasNoneAddressParams(query(url))) return query(url).keep(...LOCATION_PROPS, KEY_VERTICAL);
		return query(url).keep(...LOCATION_PROPS).set(KEY_VERTICAL, Vertical.Restaurant);
	}

	const subShowPDP = showPDP$.subscribe(); // workaround for svelte 5 late subscription issue
	onDestroy(() => {
		subShowPDP.unsubscribe();
		navMainItemsSub.unsubscribe();
	});
</script>

<SearchInput currentPageUrl={page.url}/>

<Sidenav page={page} bind:open={openMenuModal}>
	{#snippet header()}
		{#if $landingItems$}
			{@const {store} = $landingItems$}
			{#if store}
				<SidenavHeader 
					name={store.name} vertical="groc" 
					estimate={store.open ? store.landing_notifications.store_notification : 'Not available'} 
					closing={store.open ? store.operating_hours : store.landing_notifications?.warning_subtitle}>
					<button class="label-m medium m-0" on:click={() => showStoreSelect = true}>Change store</button>
				</SidenavHeader>
			{/if}
		{/if}
	{/snippet}
	<!-- <div slot="header">
	</div> -->
	{#if $navState$}
		{@const items = $navState$.items}
			{#if items?.length}
				{#each items as { href, isActive, title, icon, scroll, items:subitems }}

					<a href={href} data-sveltekit-noscroll={!scroll} class="label-xl medium ellipses desktop-only" class:active={isActive}>
						{#if icon}
							<span class={icon} />
						{/if}
						{title}
					</a>
					<a href={href} data-sveltekit-noscroll={!scroll} class="label-xl book ellipses mobile-only" class:active={isActive} on:click={closeContextMenu}>
						{#if icon}
							<span class={icon} />
						{/if}
						{title}
					</a>

					{#if subitems && isActive || subitems?.find(item => item.isActive)}
						<div class="subitems">
							{#each subitems as { href, isActive, title, scroll }}
							<a href={href} data-sveltekit-noscroll={!scroll} class="label-xl medium mobile-only" class:active={isActive} on:click={closeContextMenu}>{title}</a>
							<a href={href} data-sveltekit-noscroll={!scroll} class="label-xl medium desktop-only" class:active={isActive}>{title}</a>
							{/each}
						</div>
					{/if}

				{/each}
			{/if}
		{/if}
		<!-- <svelte:fragment slot="categories">&nbsp;</svelte:fragment> -->
		{#snippet categories()}{/snippet}
</Sidenav>

{#if showStoreSelect}
	<Modal on:close={() => showStoreSelect = false}>
		<div slot="header">Select a PnP store</div>
		<StoreSelect currentPageUrl={page.url} selected={() => showStoreSelect = false} />
	</Modal>
{/if}

<div>
	{#if showSearch}
		<GrocSearch urlSearchParams={params} selectedStore={$selectedStore$} />
		
		{#if $showPDP$}
			<GrocPDP />
			<!-- <Modal on:close={closeGrocSearch} size='large' id="groc-search">
			</Modal> -->
		{/if}
	{:else if $landingItems$}
		{@const {store, loading, payload, error} = $landingItems$}
		{@const sections = payload?.layout ?? []}
		<!-- TODO: This seems to be an inactive feature (MRDP-35) -->
		<!-- {@const promo = payload?.promotion_banner} -->
		{@const buyagain = page.url.searchParams.has(BUY_AGAIN)}
		{@const occasion = page.url.searchParams.get(KEY_OCCASION)}
		{@const banner = page.url.searchParams.get(KEY_BANNER)}

		<div class="full-width">
			{#if loading}
				<CarouselV2 cache={{loading: true}} type='banners large'>
					{#snippet children()}{/snippet}
				</CarouselV2>
			{:else if !loading && !store}
				<OutOfRange />
			<!-- POPUPS -->
			{:else if buyagain}
				<UserSpecificAisle {selectedStore$} on:product={e => showDetail(e.detail)} />
			{:else}
		
				{#if store?.landing_notifications}
					<Notification data={store?.landing_notifications} />
				{/if}
		
				{#if !query(page.url).hasAny(KEY_AISLE, KEY_OCCASION, KEY_BANNER)}
					<h1 class="display-m bold">Pick n Pay</h1>
				{/if}
		
				{#if sections}
					{@const aisleId = page.url.searchParams.get(KEY_AISLE)}
					{#each sections as { type, title, sort, endpoint } (type)}
						{#if type === GroceryLandingSection.Banners && !occasion && ((banner && aisleId) || !aisleId)}
							<Banners {endpoint} id={banner} pageUrl={page.url} on:product={e => showDetail(e.detail)} />
						{:else if type === GroceryLandingSection.Aisles && !occasion && !banner}
							{@const loader = $aisleDataLoader$}
							<Aisles selected={aisleId} loader={loader} on:product={e => showDetail(e.detail)} on:subnav={({detail}) => sideNavManager.setActiveSubItems(detail.id, detail.items)}/>
						{:else if type === GroceryLandingSection.Occasions && !banner && ((occasion && aisleId) || !aisleId)}
							<Occasions endpoint={endpoint} store={store?.id??''} {occasion} on:product={e => showDetail(e.detail)} />
						{:else if !aisleId && !occasion && !banner}
							{#if type === GroceryLandingSection.BuyAgain}
								<!-- grid view: `buy_again=true`-->
								<BuyAgain data={{ type, title, sort, endpoint }} {page} on:product={e => showDetail(e.detail)} />
							{:else if type === GroceryLandingSection.SavedItems}
								<!-- grid view: `buy_again=true&filter=-2`-->
								<SavedItems data={{ type, title, sort, endpoint }} {page} on:product={e => showDetail(e.detail)} />
							{:else if type === GroceryLandingSection.Deals}
								<!-- grid view: `aisle=187`-->
								<Deals data={{ title, endpoint }} pageUrl={page.url} on:product={e => showDetail(e.detail)} getGridViewUrl={(url, comp) => insertSideNavUrl(type, title, url, comp)}/>
							{:else}
								<svelte:component this={componentFromType(type)} type={type} endpoint={endpoint} />
							{/if}
						{/if}
					{/each}
				{/if}
			{/if}
		</div>
	{:else}
		<LoadingIcons />
	{/if}
</div>
